import * as React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MarkEmailRead, PermPhoneMsg } from '@mui/icons-material';
import PrivacyPolicyText from '../../../component/PrivacyPolicyText';
import { useDispatch, useSelector } from 'react-redux';
import { verify, resendCode } from 'api/auth';
import { set_message } from 'redux/reducer/message';
import Section from '../home/components/Section';
import { otpDigitFieldSx, getOtpDigitInputProps, OTP_AUTOFILL_INPUT_PROPS, scheduleOtpAutofocus } from 'component/Input/darkFieldStyles';
import { isDevTestCredentialsEnabled, TEST_OTP } from 'constants/testCredentials';
import {
  clearVerificationUser,
  loadVerificationUser,
  persistVerificationUser,
} from 'utils/verificationGate';
import { set_user } from 'redux/reducer/user';

const OTP_LENGTH = 6;

export const Verify = () => {
  const { type } = useParams();
  const isPhone = type === 'phone';
  const verificationType = isPhone ? 'Phone' : 'Email';
  const { user: storeUser } = useSelector((state) => state.user);
  const user = storeUser || loadVerificationUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [otp, setOtp] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [counter, setCount] = React.useState(0);
  const [error, setError] = React.useState(null);
  const inputRefs = React.useRef([]);
  const pendingFocusRef = React.useRef(null);
  const submittedOtpRef = React.useRef('');

  React.useEffect(() => {
    if (otp.length < OTP_LENGTH) {
      submittedOtpRef.current = '';
    }
  }, [otp]);

  React.useEffect(() => {
    document.title = `XGain | Verify ${verificationType}`;
  }, [verificationType]);

  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  React.useEffect(() => {
    if (!user) return;
    return scheduleOtpAutofocus(() => inputRefs.current[0]);
  }, [type, user]);

  const focusDigit = React.useCallback((index) => {
    if (index >= 0 && index < OTP_LENGTH) {
      inputRefs.current[index]?.focus();
      inputRefs.current[index]?.select?.();
    }
  }, []);

  const queueFocus = React.useCallback((index) => {
    pendingFocusRef.current = index;
  }, []);

  React.useLayoutEffect(() => {
    if (pendingFocusRef.current === null) return;
    const index = pendingFocusRef.current;
    pendingFocusRef.current = null;
    focusDigit(index);
  });

  const startTimer = React.useCallback(() => {
    setCount(30);
    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev === 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const getMaskedDestination = () => {
    if (!user) return '';
    if (isPhone) {
      return `XX-XXXX-${user.phone?.slice(-4) || '****'}`;
    }
    const localPart = user.email?.split('@')[0] || '';
    const hideCount = Math.max(localPart.length - 3, 0);
    return `${'*'.repeat(hideCount)}${user.email?.slice(hideCount) || ''}`;
  };

  const handleVerify = React.useCallback(() => {
    if (!user || otp.length !== OTP_LENGTH) return;
    setLoading(true);
    setError(null);

    const data = { code: otp };
    if (isPhone) {
      data.country_code = user.country_code;
      data.phone = user.phone;
    } else {
      data.email = user.email;
    }

    verify(verificationType, data)
      .then((res) => {
        const verifiedUser = res.data?.user || user;
        if (verifiedUser) {
          dispatch(set_user(verifiedUser));
          persistVerificationUser(verifiedUser);
        }
        if (isPhone) {
          setOtp('');
          navigate('/verify/email');
          return;
        }
        if (res.data?.token) {
          clearVerificationUser();
          window.setTimeout(() => {
            window.location.href = '/dashboard/main';
          }, 200);
          return;
        }
        const nextStep = res.data?.nextStep || 'phone';
        navigate(`/verify/${nextStep}`);
      })
      .catch((err) => {
        submittedOtpRef.current = '';
        if (err.response?.status < 500 && err.response?.status >= 400) {
          setError(err.response.data?.msg || err.response.data || 'Invalid code');
        } else {
          setError('Something went wrong. Please try again.');
        }
        setOtp('');
        queueFocus(0);
        setLoading(false);
      });
  }, [user, otp, isPhone, verificationType, navigate, queueFocus]);

  React.useEffect(() => {
    if (otp.length !== OTP_LENGTH || loading) return;
    if (submittedOtpRef.current === otp) return;
    submittedOtpRef.current = otp;
    handleVerify();
  }, [otp, handleVerify, loading]);

  const applyDigits = React.useCallback((digits) => {
    const next = digits.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setOtp(next);
    queueFocus(Math.min(next.length, OTP_LENGTH - 1));
  }, [queueFocus]);

  const handleKeyDown = (event, index) => {
    const { key } = event;

    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      setOtp((prev) => {
        if (prev[index]) {
          return prev.slice(0, index) + prev.slice(index + 1);
        }
        if (index > 0) {
          queueFocus(index - 1);
          return prev.slice(0, index - 1) + prev.slice(index);
        }
        return '';
      });
      return;
    }

    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      setOtp((prev) => (
        (prev.slice(0, index) + key + prev.slice(index + 1)).slice(0, OTP_LENGTH)
      ));
      if (index < OTP_LENGTH - 1) {
        queueFocus(index + 1);
      }
      return;
    }

    if (key === 'ArrowLeft') {
      event.preventDefault();
      focusDigit(index - 1);
      return;
    }

    if (key === 'ArrowRight') {
      event.preventDefault();
      focusDigit(index + 1);
      return;
    }

    if (key.length === 1) {
      event.preventDefault();
    }
  };

  const handleChange = (event, index) => {
    const digit = event.target.value.replace(/\D/g, '').slice(-1);
    if (!digit) return;
    setOtp((prev) => (
      (prev.slice(0, index) + digit + prev.slice(index + 1)).slice(0, OTP_LENGTH)
    ));
    if (index < OTP_LENGTH - 1) {
      queueFocus(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    if (pasted) applyDigits(pasted);
  };

  const resend_code = () => {
    if (counter || !user) return;
    setOtp('');
    setError(null);
    queueFocus(0);
    const username = isPhone ? `${user.country_code} ${user.phone}` : user.email;
    resendCode(username)
      .then(() => {
        startTimer();
        dispatch(set_message(`CODE sent to ${username}`));
      })
      .catch((err) => dispatch(set_message(err.response?.data?.msg || 'Unable to resend code')));
  };

  const renderDigit = (index) => (
    <TextField
      key={index}
      variant="outlined"
      autoFocus={index === 0}
      error={Boolean(error)}
      disabled={loading}
      onKeyDown={(event) => handleKeyDown(event, index)}
      onChange={(event) => handleChange(event, index)}
      onPaste={handlePaste}
      type="tel"
      name={`verify-${index + 1}`}
      value={otp[index] || ''}
      inputRef={(el) => { inputRefs.current[index] = el; }}
      inputProps={getOtpDigitInputProps(index)}
      sx={otpDigitFieldSx}
    />
  );

  if (!user) return null;

  return (
    <Section compact sx={{ bgcolor: '#21232E', pb: 4 }}>
      <Box
        component="form"
        className="login-form-dark"
        sx={{
          maxWidth: 400,
          width: '92%',
          color: '#fff',
        }}
        mx="auto"
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
      >
        <Typography sx={{ textAlign: 'center', color: '#fff', fontWeight: 600, mt: { xs: 6, md: 8 } }} variant="h6">
          Check Your {verificationType}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          {isPhone ? (
            <PermPhoneMsg sx={{ fontSize: 72, color: '#95F205' }} />
          ) : (
            <MarkEmailRead sx={{ fontSize: 72, color: '#95F205' }} />
          )}
        </Box>

        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.85)' }}>
          Verification code sent to
        </Typography>
        <Typography sx={{ textAlign: 'center', color: '#95F205', fontWeight: 700, mt: 0.5 }}>
          {getMaskedDestination()}
        </Typography>
        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.85)', mt: 1 }}>
          Please enter the CODE below.
        </Typography>

        {isDevTestCredentialsEnabled && (
          <Typography sx={{ color: '#93c5fd', textAlign: 'center', mt: 1, fontSize: 13 }}>
            Dev OTP: {TEST_OTP}
          </Typography>
        )}

        {error && (
          <Typography sx={{ color: '#f87171', textAlign: 'center', mt: 2 }}>
            {typeof error === 'string' ? error : 'Invalid verification code'}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 1 }, mt: 2, position: 'relative' }}>
          <input
            {...OTP_AUTOFILL_INPUT_PROPS}
            aria-hidden="true"
            tabIndex={-1}
            onChange={(event) => applyDigits(event.target.value)}
            style={{
              position: 'absolute',
              opacity: 0,
              width: 1,
              height: 1,
              border: 0,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
            }}
          />
          {Array.from({ length: OTP_LENGTH }, (_, index) => renderDigit(index))}
        </Box>

        <Box my={3} textAlign="center">
          <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Didn&apos;t receive it?{' '}
            {counter ? (
              <Typography component="span" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                Please retry in {counter}s
              </Typography>
            ) : (
              <Button
                onClick={resend_code}
                sx={{
                  color: '#60a5fa',
                  textTransform: 'none',
                  p: 0,
                  minWidth: 0,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                }}
              >
                Resend
              </Button>
            )}{' '}
            my verification code
          </Typography>
        </Box>

        <Box my={2} textAlign="center">
          <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Have an account?
            <Link to="/login" style={{ marginLeft: 4, color: '#60a5fa', textDecoration: 'none' }}>
              Login
            </Link>
          </Typography>
        </Box>

        <Box sx={{ color: 'rgba(255,255,255,0.75)', '& a': { color: '#60a5fa' } }}>
          <PrivacyPolicyText />
        </Box>
      </Box>
    </Section>
  );
};

export default Verify;
